import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, apiUrl } from '../api/client.js';
import { useOllamaStatus } from '../api/hooks.js';
import type { LlmProvider } from '../api/types.js';
import { Badge, Card, ConfirmDialog, PageTitle } from '../components/ui.js';
import { Button } from '../components/ui/Button.js';
import { Input, Select } from '../components/ui/Input.js';
import { llmModelOptions } from '../lib/llm-models.js';
import { getAiToken, setAiToken } from '../lib/auth.js';
import { copyText, maskToken } from '../lib/format.js';

interface ProviderMeta {
  label: string;
  num: number;
  keyLabel: string;
  keyPlaceholder: string;
  accountIdLabel: string | null;
  link: string;
  linkText: string;
  models: string[];
  needs: string;
  tips: string[];
}

const PROVIDER_META: Record<string, ProviderMeta> = {
  openrouter: {
    label: 'OpenRouter',
    num: 1,
    keyLabel: 'API Key',
    keyPlaceholder: 'sk-or-...',
    accountIdLabel: null,
    link: 'https://openrouter.ai/settings/keys',
    linkText: 'Get free API key',
    models: ['openrouter/free', 'google/gemma-4-26b-a4b-it:free', 'openai/gpt-oss-20b:free'],
    needs: 'API key only. Free models work at $0 balance.',
    tips: ['Recommended default for Loom.', 'Use model IDs ending in :free, or openrouter/free.'],
  },
  groq: {
    label: 'Groq',
    num: 2,
    keyLabel: 'API Key',
    keyPlaceholder: 'gsk_...',
    accountIdLabel: null,
    link: 'https://console.groq.com/keys',
    linkText: 'Get free API key',
    models: ['openai/gpt-oss-120b', 'openai/gpt-oss-20b', 'llama-3.3-70b-versatile'],
    needs: 'API key only. Strong free tool-calling models (GPT-OSS / Llama).',
    tips: [
      'Prefer openai/gpt-oss-120b for MCP / agent testing.',
      'Some Coolify/datacenter egress IPs get WAF 403 — use Test; retry after Taiwan host move if blocked.',
      'Loom wire ID: groq/<model-id>, e.g. groq/openai/gpt-oss-120b.',
    ],
  },
  cloudflare: {
    label: 'Cloudflare Workers AI',
    num: 3,
    keyLabel: 'API Token',
    keyPlaceholder: 'token',
    accountIdLabel: 'Account ID',
    link: 'https://dash.cloudflare.com/?to=/:account/ai/workers-ai',
    linkText: 'Workers AI → Create token',
    models: ['@cf/meta/llama-3.2-3b-instruct', '@cf/meta/llama-3.1-8b-instruct-fp8'],
    needs: 'API Token with Workers AI Read/Edit + Account ID.',
    tips: ['Account ID is on any Cloudflare dashboard Overview page.'],
  },
  gemini: {
    label: 'Google Gemini',
    num: 4,
    keyLabel: 'API Key',
    keyPlaceholder: 'AIza…',
    accountIdLabel: null,
    link: 'https://aistudio.google.com/apikey',
    linkText: 'Get free API key',
    models: ['gemini-2.0-flash', 'gemini-2.0-flash-lite'],
    needs: 'API key from AI Studio.',
    tips: ['Create key at aistudio.google.com.'],
  },
  'ollama-cloud': {
    label: 'Ollama Cloud',
    num: 5,
    keyLabel: 'API Key',
    keyPlaceholder: 'ollama-…',
    accountIdLabel: null,
    link: 'https://ollama.com/settings/keys',
    linkText: 'Get Ollama Cloud API key',
    models: ['gpt-oss:120b', 'deepseek-v3.1:671b'],
    needs: 'Ollama Cloud API key.',
    tips: ['Use ollama-cloud/<id> in Cursor / Personal Bot.'],
  },
};

export function Models() {
  const queryClient = useQueryClient();
  const ollama = useOllamaStatus();
  const providers = useQuery({
    queryKey: ['llm-providers'],
    queryFn: () => api.get<Record<string, LlmProvider>>('/api/user/llm-providers'),
  });
  const models = useQuery({
    queryKey: ['llm-models'],
    queryFn: () => api.get<Record<string, string[]>>('/api/user/llm-models'),
  });
  const [open, setOpen] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatModel, setChatModel] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatOut, setChatOut] = useState('');
  const [removeName, setRemoveName] = useState<string | null>(null);
  const v1 = `${apiUrl('/v1')}`;

  async function save(name: string, apiKey: string, accountId?: string) {
    await api.post(`/api/user/llm-providers/${name}`, {
      apiKey,
      ...(accountId ? { accountId } : {}),
    });
    toast.success('Saved');
    void queryClient.invalidateQueries({ queryKey: ['llm-providers'] });
    void queryClient.invalidateQueries({ queryKey: ['llm-models'] });
  }

  async function test(name: string) {
    const r = await api.post<{ ok: boolean; error?: string; latencyMs?: number }>(
      `/api/user/llm-providers/${name}/test`,
    );
    if (r.ok) toast.success(`OK${r.latencyMs ? ` · ${r.latencyMs}ms` : ''}`);
    else toast.error(r.error ?? 'Test failed');
  }

  async function chat() {
    let jwt = getAiToken();
    if (!jwt) {
      const data = await api.get<{ token: string | null }>('/api/ai-token').catch(() => ({ token: null }));
      if (data.token) {
        setAiToken(data.token);
        jwt = data.token;
      }
    }
    if (!jwt || !chatModel) {
      toast.error('Need an AI token and a model');
      return;
    }
    const r = await fetch(apiUrl('/v1/chat/completions'), {
      method: 'POST',
      headers: { 'content-type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({
        model: chatModel,
        messages: [{ role: 'user', content: chatInput || 'Hi' }],
        max_tokens: 128,
      }),
    });
    const body = (await r.json()) as { choices?: { message?: { content?: string } }[]; error?: { message?: string } };
    setChatOut(body.choices?.[0]?.message?.content ?? body.error?.message ?? JSON.stringify(body));
  }

  const live = models.data ?? {};
  const cfg = providers.data ?? {};
  const allModels = llmModelOptions(live as Record<string, unknown>);

  return (
    <div>
      <PageTitle title="AI Models" sub="Connect free LLM providers. Use Loom as an OpenAI-compatible endpoint." />

      <Card className="mb-4 p-4 text-sm">
        <strong>Cursor setup:</strong> save a provider key below, then{' '}
        <Link to="/connect" className="text-[var(--color-accent)]">
          AI Config
        </Link>{' '}
        → copy JWT. Base URL = <code className="rounded bg-[var(--color-surface-3)] px-1">{v1}</code>, API
        Key = that JWT (not the provider key).
      </Card>

      <Card className="mb-5 flex flex-wrap gap-4 p-4">
        <div className="min-w-52 flex-1 text-sm text-[var(--color-ink-muted)]">
          Loom as model brain: Cursor, Personal Bot, and scripts. Claude / Codex: MCP only.
        </div>
        <pre className="rounded-md bg-[var(--color-surface-2)] p-3 font-mono text-xs">
          OpenAI: {v1}
          {'\n'}Auth: Bearer &lt;your-loom-jwt&gt;
          {'\n'}Model: openrouter/openrouter/free
        </pre>
      </Card>

      {Object.entries(PROVIDER_META).map(([name, meta]) => {
        const saved = cfg[name];
        const liveModels = live[name] ?? [];
        const ready = !!saved && liveModels.length > 0;
        return (
          <ProviderCard
            key={name}
            name={name}
            meta={meta}
            saved={saved}
            liveModels={liveModels}
            ready={ready}
            expanded={open === name}
            onToggle={() => setOpen(open === name ? null : name)}
            onSave={(key, accountId) => void save(name, key, accountId).catch((err: Error) => toast.error(err.message))}
            onTest={() => void test(name).catch((err: Error) => toast.error(err.message))}
            onRemove={() => setRemoveName(name)}
            v1={v1}
          />
        );
      })}

      <Card className="mb-2 p-4">
        <button type="button" className="flex w-full items-center gap-3 text-left" onClick={() => setOpen(open === 'ollama' ? null : 'ollama')}>
          <span
            className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
            style={{ background: 'var(--color-accent)' }}
          >
            5
          </span>
          <div className="flex-1">
            <div className="text-sm font-semibold">Ollama (company server)</div>
            <div className="text-xs text-[var(--color-ink-faint)]">ollama/llama3.2</div>
          </div>
          <Badge tone={ollama.data?.available ? 'good' : 'bad'}>
            {ollama.data?.available ? 'Server ready' : 'Not on this server'}
          </Badge>
        </button>
        {open === 'ollama' ? (
          <div className="mt-3 border-t border-[var(--color-line)] pt-3 text-sm text-[var(--color-ink-muted)]">
            {ollama.data?.available
              ? (live.ollama?.length
                  ? live.ollama.map((m) => (
                      <button
                        key={m}
                        type="button"
                        className="mr-1 mb-1 rounded-md px-2 py-1 font-mono text-xs"
                        style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent)' }}
                        onClick={() => void copyText(`ollama/${m}`).then(() => toast.success('Copied'))}
                      >
                        {m}
                      </button>
                    ))
                  : 'Ollama is running but no models are installed yet.')
              : 'Ask an administrator to set OLLAMA_BASE_URL.'}
          </div>
        ) : null}
      </Card>

      <Card className="mt-6 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold">Try it out — chat tester</div>
            <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
              Send a quick message through Loom. Needs at least one Ready provider.
            </p>
          </div>
          <Button size="sm" onClick={() => setChatOpen((v) => !v)}>
            {chatOpen ? 'Close' : 'Open tester'}
          </Button>
        </div>
        {chatOpen ? (
          <div className="mt-3 space-y-2 border-t border-[var(--color-line)] pt-3">
            <Select value={chatModel} onChange={(e) => setChatModel(e.target.value)}>
              <option value="">Choose a model…</option>
              {allModels.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
            <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="Say hello…" />
            <Button size="sm" onClick={() => void chat().catch((err: Error) => toast.error(err.message))}>
              Send
            </Button>
            {chatOut ? <pre className="rounded-md bg-[var(--color-surface-2)] p-3 text-xs">{chatOut}</pre> : null}
          </div>
        ) : null}
      </Card>

      {removeName ? (
        <ConfirmDialog
          title={`Remove ${removeName}?`}
          body="The saved key will be deleted from Loom."
          confirmText="Remove"
          onClose={() => setRemoveName(null)}
          onConfirm={() => {
            const name = removeName;
            setRemoveName(null);
            void api.delete(`/api/user/llm-providers/${name}`).then(() => {
              toast.success('Removed');
              void queryClient.invalidateQueries({ queryKey: ['llm-providers'] });
              void queryClient.invalidateQueries({ queryKey: ['llm-models'] });
            });
          }}
        />
      ) : null}
    </div>
  );
}

function ProviderCard({
  name,
  meta,
  saved,
  liveModels,
  ready,
  expanded,
  onToggle,
  onSave,
  onTest,
  onRemove,
  v1,
}: {
  name: string;
  meta: ProviderMeta;
  saved?: LlmProvider;
  liveModels: string[];
  ready: boolean;
  expanded: boolean;
  onToggle: () => void;
  onSave: (key: string, accountId?: string) => void;
  onTest: () => void;
  onRemove: () => void;
  v1: string;
}) {
  const [key, setKey] = useState('');
  const [accountId, setAccountId] = useState(saved?.accountId ?? '');
  const sample = liveModels[0] || meta.models[0];
  const jwt = getAiToken();

  return (
    <Card className="mb-2 p-4">
      <button type="button" className="flex w-full items-center gap-3 text-left" onClick={onToggle}>
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
          style={{ background: 'var(--color-accent)' }}
        >
          {meta.num}
        </span>
        <div className="flex-1">
          <div className="text-sm font-semibold">{meta.label}</div>
          <div className="text-xs text-[var(--color-ink-faint)]">
            {meta.models.slice(0, 2).map((m) => `${name}/${m}`).join('  ')}
          </div>
        </div>
        {saved?.apiKey ? <span className="font-mono text-xs text-[var(--color-ink-faint)]">{saved.apiKey}</span> : null}
        <Badge tone={!saved ? 'neutral' : ready ? 'good' : 'warn'}>
          {!saved ? 'Not configured' : ready ? `Ready · ${liveModels.length} models` : 'Saved — verify'}
        </Badge>
      </button>
      {expanded ? (
        <div className="mt-3 space-y-3 border-t border-[var(--color-line)] pt-3">
          <p className="text-xs text-[var(--color-ink-muted)]">{meta.needs}</p>
          <ul className="list-disc pl-4 text-xs text-[var(--color-ink-faint)]">
            {meta.tips.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
          {liveModels.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {liveModels.map((m) => (
                <button
                  key={m}
                  type="button"
                  className="rounded-md px-2 py-1 font-mono text-xs"
                  style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent)' }}
                  onClick={() => void copyText(`${name}/${m}`).then(() => toast.success('Copied'))}
                >
                  {m}
                </button>
              ))}
            </div>
          ) : saved ? (
            <p className="text-xs text-[var(--color-star)]">Key saved, but Loom got no models. Click Test.</p>
          ) : null}
          <label className="block text-xs text-[var(--color-ink-muted)]">
            {meta.keyLabel} —{' '}
            <a href={meta.link} target="_blank" rel="noreferrer" className="text-[var(--color-accent)]">
              {meta.linkText}
            </a>
          </label>
          <Input type="password" placeholder={meta.keyPlaceholder} value={key} onChange={(e) => setKey(e.target.value)} />
          {meta.accountIdLabel ? (
            <Input
              placeholder={meta.accountIdLabel}
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            />
          ) : null}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onSave(key, accountId || undefined)}>
              Save
            </Button>
            {saved ? (
              <Button variant="secondary" size="sm" onClick={onTest}>
                Test
              </Button>
            ) : null}
            {saved ? (
              <Button variant="danger" size="sm" onClick={onRemove}>
                Remove
              </Button>
            ) : null}
          </div>
          {saved ? (
            <div className="rounded-md bg-[var(--color-surface-2)] p-3 font-mono text-xs">
              Base URL: {v1}
              <br />
              API Key: {jwt ? maskToken(jwt) : 'Generate in AI Config'}
              <br />
              Model: {name}/{sample}
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
